import React, { useState, useEffect, useRef } from 'react';
import { Send, ChevronLeft, ChevronRight, Wifi, Battery, Signal, Search, SquarePen } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8000';

// Pre-defined skill chats
const SKILL_CHATS = [
  {
    id: 'fig-main',
    name: 'Fig',
    avatar: 'https://customer-assets.emergentagent.com/job_path-explorer-12/artifacts/cyj4b8vo_Fig%20Icon%202.png',
    preview: "what's on your mind?",
    unread: 0,
    isMain: true,
    usePersonalizedGreeting: true, // Flag to generate personalized greeting
    initialMessages: [] // Will be populated dynamically
  },
  {
    id: 'outlier-edge',
    name: 'The Outlier Edge',
    emoji: '✦',
    preview: "your unfair advantage...",
    unread: 1,
    isPhilosophyChat: true, // Flag for special styling
    initialMessages: [] // Will be populated dynamically with personalized content
  },
  {
    id: 'personal-brand',
    name: 'Personal Brand',
    emoji: '✨',
    preview: "lets make you unforgettable...",
    unread: 3,
    initialMessages: [
      { role: 'assistant', content: "your personal brand is your reputation at scale" },
      { role: 'assistant', content: "its not about being fake" },
      { role: 'assistant', content: "its about being intentionally visible with your authentic self" },
      { role: 'assistant', content: "the goal is people know what youre about before you walk into the room" },
      { role: 'assistant', content: "what part of personal branding do you wanna explore" }
    ]
  },
  {
    id: 'side-projects',
    name: 'Side Projects',
    emoji: '🛠️',
    preview: "build something people notice...",
    unread: 2,
    initialMessages: [
      { role: 'assistant', content: "side projects are your secret weapon" },
      { role: 'assistant', content: "they prove you can ship" },
      { role: 'assistant', content: "show initiative" },
      { role: 'assistant', content: "and give you stories to tell in interviews" },
      { role: 'assistant', content: "the best projects solve a real problem you actually have" },
      { role: 'assistant', content: "that passion shows through" },
      { role: 'assistant', content: "tell me about your interests and ill help brainstorm ideas" }
    ]
  },
  {
    id: 'cold-outreach',
    name: 'Cold Outreach',
    emoji: '📧',
    preview: "the art of reaching out...",
    unread: 2,
    initialMessages: [
      { role: 'assistant', content: "heres the secret to cold emails that actually get replies" },
      { role: 'assistant', content: "dont ask for things" },
      { role: 'assistant', content: "ask how you can help them" },
      { role: 'assistant', content: "like hey i loved your work on x. im exploring y and would love to support your work in any way. what are you working on that i could help with" },
      { role: 'assistant', content: "flips the whole script" },
      { role: 'assistant', content: "who do you wanna reach out to" }
    ]
  },
  {
    id: 'networking',
    name: 'Networking Events',
    emoji: '🤝',
    preview: "find your people irl...",
    unread: 2,
    initialMessages: [
      { role: 'assistant', content: "networking doesnt have to feel gross" },
      { role: 'assistant', content: "check out lu.ma for curated events in your city" },
      { role: 'assistant', content: "want something more spontaneous" },
      { role: 'assistant', content: "try 222.place or timeleft" },
      { role: 'assistant', content: "theyre meet a stranger dinners that are surprisingly great for random connections" },
      { role: 'assistant', content: "key is showing up consistently to the same communities" },
      { role: 'assistant', content: "regulars become friends. friends become opportunities" }
    ]
  },
  {
    id: 'social-media',
    name: 'Social Strategy',
    emoji: '📱',
    preview: "pick your platform wisely...",
    unread: 3,
    initialMessages: [
      { role: 'assistant', content: "posting online is basically free marketing for your career" },
      { role: 'assistant', content: "but different platforms different superpowers" },
      { role: 'assistant', content: "instagram and tiktok reach consumers. great if you wanna be an entrepreneur or creator" },
      { role: 'assistant', content: "linkedin reaches professionals. build credibility so you never need to submit a job app again" },
      { role: 'assistant', content: "x is tech and startup world. essential if youre a founder or want to be in that ecosystem" },
      { role: 'assistant', content: "which audience are you trying to reach" }
    ]
  }
];

const CoachTabContent = ({ formData }) => {
  const { user } = useAuth();
  // Only show intro if user hasn't seen it before
  const [showIntro, setShowIntro] = useState(() => {
    return !localStorage.getItem('coachIntroSeen');
  });
  const [activeChat, setActiveChat] = useState(null); // null = show list, string = show chat

  // Generate the Outlier Edge philosophy chat messages (personalized)
  const getOutlierEdgeMessages = () => {
    const userName = formData?.name || formData?.user_name || formData?.resume_data?.name || '';
    const firstName = userName ? userName.split(' ')[0] : '';
    const interests = formData?.interests || formData?.resume_data?.youtube?.interests || [];
    const skills = formData?.skills || formData?.resume_data?.skills || [];

    const messages = [
      {
        role: 'assistant',
        content: `people who stand out arent great at many things`,
        isPhilosophy: true
      },
      {
        role: 'assistant',
        content: `theyre unmistakably great at one thing`,
        isPhilosophy: true
      },
      {
        role: 'assistant',
        content: `real leverage = being known for something specific by the people around you`,
        isPhilosophy: true
      },
      {
        role: 'assistant',
        content: `but heres the thing`,
        isPhilosophy: true
      },
      {
        role: 'assistant',
        content: `you dont start with focus. you start with exploration`,
        isPhilosophy: true
      },
      {
        role: 'assistant',
        content: `following curiosity, trying paths, pattern spotting what pulls you in`,
        isPhilosophy: true
      },
      {
        role: 'assistant',
        content: `thats not indecision`,
        isPhilosophy: true
      },
      {
        role: 'assistant',
        content: `thats how you find the edge worth sharpening`,
        isPhilosophy: true
      }
    ];

    // Add personalized message based on user data
    if (firstName && interests.length > 0) {
      messages.push(
        { role: 'assistant', content: `ok ${firstName.toLowerCase()}` },
        { role: 'assistant', content: `looking at your profile you've explored ${interests.slice(0, 2).join(' and ').toLowerCase()}${skills.length > 0 ? ` and picked up ${skills[0].toLowerCase()}` : ''}` },
        { role: 'assistant', content: `where do those dots connect into something only you can do?` }
      );
    } else if (firstName) {
      messages.push(
        { role: 'assistant', content: `${firstName.toLowerCase()} youre in the exploration phase` },
        { role: 'assistant', content: `thats exactly where you should be` },
        { role: 'assistant', content: `whats something you keep coming back to?` }
      );
    } else {
      messages.push(
        { role: 'assistant', content: `youre in the exploration phase` },
        { role: 'assistant', content: `thats exactly where you should be` },
        { role: 'assistant', content: `whats something you keep coming back to?` }
      );
    }

    return messages;
  };

  // Generate personalized greeting for Fig main chat
  const getPersonalizedGreeting = () => {
    const userName = formData?.name || formData?.user_name || formData?.resume_data?.name || '';
    const firstName = userName ? userName.split(' ')[0] : '';
    const interests = formData?.interests || formData?.resume_data?.youtube?.interests || [];
    const skills = formData?.skills || formData?.resume_data?.skills || [];
    const firstInterest = interests[0] || '';
    const firstSkill = skills[0] || '';

    if (firstName && firstInterest) {
      return [
        { role: 'assistant', content: `hey ${firstName.toLowerCase()}` },
        { role: 'assistant', content: `so youre into ${firstInterest.toLowerCase()}` },
        { role: 'assistant', content: firstSkill ? `and you got skills in ${firstSkill.toLowerCase()}` : `interesting` },
        { role: 'assistant', content: `whats on your mind` }
      ];
    } else if (firstName) {
      return [
        { role: 'assistant', content: `hey ${firstName.toLowerCase()}` },
        { role: 'assistant', content: `im fig` },
        { role: 'assistant', content: `here to help you figure out your next move` },
        { role: 'assistant', content: `whats up` }
      ];
    } else {
      return [
        { role: 'assistant', content: `hey` },
        { role: 'assistant', content: `im fig` },
        { role: 'assistant', content: `here to help you figure out your next move` },
        { role: 'assistant', content: `whats on your mind` }
      ];
    }
  };

  const [chatHistories, setChatHistories] = useState(() => {
    const histories = {};
    SKILL_CHATS.forEach(chat => {
      if (chat.usePersonalizedGreeting) {
        histories[chat.id] = getPersonalizedGreeting();
      } else if (chat.isPhilosophyChat) {
        histories[chat.id] = getOutlierEdgeMessages();
      } else {
        histories[chat.id] = [...chat.initialMessages];
      }
    });
    return histories;
  });
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const messagesEndRef = useRef(null);

  // Show intro screen for 3 seconds, then mark as seen
  useEffect(() => {
    if (!showIntro) return;
    const timer = setTimeout(() => {
      setShowIntro(false);
      localStorage.setItem('coachIntroSeen', 'true');
    }, 3000);
    return () => clearTimeout(timer);
  }, [showIntro]);

  // Load chat history from database on mount
  useEffect(() => {
    if (historyLoaded) return;

    const loadChatHistory = async () => {
      try {
        const { data: { session } } = await import('../lib/supabaseClient').then(m => m.supabase.auth.getSession());

        if (!session?.access_token) {
          setHistoryLoaded(true);
          return;
        }

        const response = await fetch(`${API_BASE}/api/coach/history`, {
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          const savedHistories = data.histories || {};

          // Merge saved histories with initial messages
          setChatHistories(prev => {
            const merged = { ...prev };
            for (const chatId of Object.keys(savedHistories)) {
              if (savedHistories[chatId] && savedHistories[chatId].length > 0) {
                // Get initial messages for this chat
                const initialMessages = prev[chatId] || [];
                // Append saved messages after initial messages
                merged[chatId] = [...initialMessages, ...savedHistories[chatId]];
              }
            }
            return merged;
          });
        }
      } catch (error) {
        console.error('Failed to load coach chat history:', error);
      } finally {
        setHistoryLoaded(true);
      }
    };

    loadChatHistory();
  }, [historyLoaded]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatHistories, activeChat]);

  // Local fallback responses when API is unavailable
  const getLocalFallbackResponse = (message, chatContext) => {
    const lowerMsg = message.toLowerCase();
    const chatName = chatContext?.name?.toLowerCase() || 'general';

    // Context-aware fallback responses
    if (chatName.includes('brand')) {
      if (lowerMsg.includes('start') || lowerMsg.includes('begin') || lowerMsg.includes('how')) {
        return "start with what makes you different ||| what do people come to you for advice on? ||| that's usually your brand foundation";
      }
      return "personal branding is about being intentionally visible ||| think about what you want to be known for ||| then show up consistently with that message";
    }
    if (chatName.includes('project')) {
      if (lowerMsg.includes('idea')) {
        return "best project ideas solve a problem you actually have ||| what annoys you that you could fix? ||| that passion shows through";
      }
      return "side projects prove you can ship ||| pick something small you can finish in a weekend ||| done is better than perfect";
    }
    if (chatName.includes('outreach') || chatName.includes('cold')) {
      return "the secret to cold emails ||| dont ask for things, ask how you can help them ||| flip the script and watch the replies roll in";
    }
    if (chatName.includes('network')) {
      return "networking doesnt have to feel gross ||| show up consistently to the same communities ||| regulars become friends, friends become opportunities";
    }
    if (chatName.includes('social')) {
      return "different platforms, different superpowers ||| linkedin for pros, x for tech/startups, ig/tiktok for consumers ||| pick based on who you want to reach";
    }
    if (chatName.includes('outlier')) {
      return "people who stand out arent great at many things ||| theyre unmistakably great at one thing ||| what could be your one thing?";
    }

    // General Fig responses
    const generalResponses = [
      "interesting ||| tell me more about what you're thinking",
      "i hear you ||| what feels like the biggest blocker right now?",
      "that makes sense ||| what would success look like for you here?",
      "got it ||| have you tried breaking it down into smaller steps?",
      "yeah thats a common challenge ||| what's worked for you in the past?"
    ];
    return generalResponses[Math.floor(Math.random() * generalResponses.length)];
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || !activeChat || isTyping) return;

    const userMsg = { role: 'user', content: inputValue };
    const currentChatId = activeChat;
    const chatContext = SKILL_CHATS.find(c => c.id === activeChat);

    // Update state and clear input immediately
    setChatHistories(prev => ({
      ...prev,
      [currentChatId]: [...prev[currentChatId], userMsg]
    }));
    setInputValue('');
    setIsTyping(true);

    // Let React render the user message before starting the API call
    await new Promise(resolve => setTimeout(resolve, 0));

    // Retry logic with timeout
    const maxRetries = 2;
    let lastError = null;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const { data: { session } } = await import('../lib/supabaseClient').then(m => m.supabase.auth.getSession());

        // Skip API call if no session - use local fallback
        if (!session?.access_token) {
          console.warn('No auth session, using local fallback');
          throw new Error('No auth session');
        }

        const skillContext = chatContext?.isMain ? '' : `The user is asking about ${chatContext?.name}. `;

        // Create abort controller for timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout

        // Get current chat history (excluding the user message we just added)
        const currentHistory = chatHistories[currentChatId] || [];

        const response = await fetch(`${API_BASE}/api/coach/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({
            message: userMsg.content,
            current_context: formData,
            skill_context: skillContext,
            chat_history: currentHistory.map(m => ({ role: m.role, content: m.content }))
          }),
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }

        const data = await response.json();
        const responseText = data.response || '';
        const parts = responseText.split('|||').map(p => p.trim()).filter(p => p);

        // If response is empty, use fallback
        if (parts.length === 0) {
          throw new Error('Empty response');
        }

        const newMessages = parts.map((part, i) => ({
          role: 'assistant',
          content: part,
          actions: i === parts.length - 1 ? data.actions : undefined
        }));

        // Use captured currentChatId to avoid stale closure if user switched chats
        setChatHistories(prev => ({
          ...prev,
          [currentChatId]: [...(prev[currentChatId] || []), ...newMessages]
        }));

        setIsTyping(false);
        return; // Success - exit function

      } catch (error) {
        lastError = error;
        console.warn(`Coach attempt ${attempt + 1} failed:`, error.message);

        // If it's a timeout or network error, retry
        if (attempt < maxRetries && (error.name === 'AbortError' || error.message.includes('fetch'))) {
          await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second before retry
          continue;
        }

        // If all retries failed or it's a non-retryable error, use local fallback
        break;
      }
    }

    // All retries failed - use local fallback
    const fallbackResponse = getLocalFallbackResponse(userMsg.content, chatContext);
    const fallbackParts = fallbackResponse.split('|||').map(p => p.trim()).filter(p => p);

    const fallbackMessages = fallbackParts.map(part => ({
      role: 'assistant',
      content: part
    }));

    // Use captured currentChatId to avoid stale closure if user switched chats
    setChatHistories(prev => ({
      ...prev,
      [currentChatId]: [...(prev[currentChatId] || []), ...fallbackMessages]
    }));

    setIsTyping(false);
  };

  const currentTime = new Date().toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });

  const currentChat = SKILL_CHATS.find(c => c.id === activeChat);
  const messages = activeChat ? chatHistories[activeChat] : [];
  const totalUnread = SKILL_CHATS.reduce((sum, chat) => sum + chat.unread, 0);

  return (
    <div className="relative mx-auto" style={{ maxWidth: '460px' }}>
      {/* iPhone Frame */}
      <div
        className="relative overflow-hidden"
        style={{
          background: '#000',
          borderRadius: '48px',
          padding: '12px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 0 0 3px #1a1a1a',
        }}
      >
        {/* Inner Screen */}
        <div
          className="relative overflow-hidden flex flex-col"
          style={{
            background: '#f2f2f7',
            borderRadius: '40px',
            height: '700px',
          }}
        >
          {/* Dynamic Island */}
          <div
            className="absolute top-2 left-1/2 -translate-x-1/2 z-50"
            style={{
              width: '126px',
              height: '34px',
              background: '#000',
              borderRadius: '20px',
            }}
          />

          {/* Intro Screen Overlay */}
          <AnimatePresence>
            {showIntro && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="absolute inset-0 z-40 flex flex-col items-center justify-center p-6 text-center"
                style={{
                  background: 'linear-gradient(135deg, #FFF9F0 0%, #FFFEF9 100%)',
                  borderRadius: '40px',
                }}
              >
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.6 }}
                  className="max-w-[300px]"
                >
                  <h2 className="text-2xl font-black text-black mb-3" style={{ fontFamily: "'Montserrat', sans-serif", lineHeight: 1.2 }}>
                    Now chit chat with our underpaid intern for like 2 minutes
                  </h2>
                  <p className="text-base text-gray-600" style={{ fontFamily: "'Inter', sans-serif", lineHeight: 1.5 }}>
                    (they claim to be an expert but you can be the judge of that and let us know so we can bump up their pay)
                  </p>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Status Bar */}
          <div
            className="flex items-center justify-between px-8 pt-3 pb-1"
            style={{ paddingTop: '14px', background: '#f2f2f7' }}
          >
            <span className="text-sm font-semibold text-black" style={{ marginLeft: '-8px' }}>
              {currentTime}
            </span>
            <div className="flex items-center gap-1" style={{ marginRight: '-8px' }}>
              <Signal size={16} className="text-black" />
              <Wifi size={16} className="text-black" />
              <Battery size={22} className="text-black" />
            </div>
          </div>

          <AnimatePresence mode="wait">
            {!activeChat ? (
              /* Chat List View */
              <motion.div
                key="list"
                initial={{ x: -100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -100, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="flex-1 flex flex-col overflow-hidden"
              >
                {/* Messages Header */}
                <div className="px-4 pt-2 pb-2">
                  <div className="flex items-center justify-between mb-2">
                    <button className="text-[#007AFF] text-[17px]">Edit</button>
                    <h1 className="text-[22px] font-bold text-black">Messages</h1>
                    <SquarePen size={22} className="text-[#007AFF]" />
                  </div>

                  {/* Search Bar */}
                  <div
                    className="flex items-center gap-2 px-3 py-2 rounded-xl"
                    style={{ background: 'rgba(118, 118, 128, 0.12)' }}
                  >
                    <Search size={16} className="text-gray-400" />
                    <span className="text-gray-400 text-[16px]">Search</span>
                  </div>
                </div>

                {/* Chat List */}
                <div className="flex-1 overflow-y-auto">
                  {SKILL_CHATS.map((chat) => (
                    <motion.button
                      key={chat.id}
                      onClick={() => setActiveChat(chat.id)}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-100 transition-colors text-left border-b border-gray-200"
                      whileTap={{ scale: 0.98 }}
                    >
                      {chat.avatar ? (
                        <img
                          src={chat.avatar}
                          alt={chat.name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <div
                          className="w-12 h-12 rounded-full flex items-center justify-center text-2xl"
                          style={{
                            background: chat.isPhilosophyChat
                              ? 'linear-gradient(135deg, #1a1a1a 0%, #333 100%)'
                              : 'linear-gradient(135deg, #007AFF 0%, #5856D6 100%)',
                            border: chat.isPhilosophyChat ? '2px solid #FFE566' : 'none',
                            color: chat.isPhilosophyChat ? '#FFE566' : 'white'
                          }}
                        >
                          {chat.emoji}
                        </div>
                      )}

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-black text-[16px]">{chat.name}</span>
                          <div className="flex items-center gap-1">
                            <span className="text-gray-400 text-[14px]">now</span>
                            <ChevronRight size={16} className="text-gray-300" />
                          </div>
                        </div>
                        <p className="text-gray-500 text-[14px] truncate">{chat.preview}</p>
                      </div>

                      {chat.unread > 0 && (
                        <div
                          className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                          style={{ background: '#007AFF' }}
                        >
                          {chat.unread}
                        </div>
                      )}
                    </motion.button>
                  ))}
                </div>

                {/* Home Indicator */}
                <div className="flex justify-center py-2">
                  <div className="w-32 h-1 rounded-full" style={{ background: '#000' }} />
                </div>
              </motion.div>
            ) : (
              /* Individual Chat View */
              <motion.div
                key="chat"
                initial={{ x: 100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: 100, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="flex-1 flex flex-col overflow-hidden"
              >
                {/* iMessage Header */}
                <div
                  className="flex items-center px-4 py-3 border-b"
                  style={{
                    background: 'rgba(242, 242, 247, 0.9)',
                    backdropFilter: 'blur(20px)',
                    borderColor: 'rgba(0,0,0,0.1)',
                  }}
                >
                  <button
                    onClick={() => setActiveChat(null)}
                    className="flex items-center gap-0.5 text-[#007AFF] font-medium"
                  >
                    <ChevronLeft size={28} strokeWidth={2.5} />
                    <span className="text-[17px]">{totalUnread}</span>
                  </button>

                  <div className="flex-1 flex flex-col items-center -ml-4">
                    {currentChat?.avatar ? (
                      <img
                        src={currentChat.avatar}
                        alt={currentChat.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center text-xl"
                        style={{
                          background: currentChat?.isPhilosophyChat
                            ? 'linear-gradient(135deg, #1a1a1a 0%, #333 100%)'
                            : 'linear-gradient(135deg, #007AFF 0%, #5856D6 100%)',
                          border: currentChat?.isPhilosophyChat ? '2px solid #FFE566' : 'none',
                          color: currentChat?.isPhilosophyChat ? '#FFE566' : 'white'
                        }}
                      >
                        {currentChat?.emoji}
                      </div>
                    )}
                    <span className="text-xs font-medium text-black mt-0.5">{currentChat?.name}</span>
                  </div>

                  <div className="w-16" />
                </div>

                {/* Messages Area */}
                <div
                  className="flex-1 overflow-y-auto px-3 py-3 space-y-1"
                  style={{ background: '#f2f2f7' }}
                >
                  <AnimatePresence>
                    {messages.map((msg, idx) => (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ duration: 0.2 }}
                        className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        {msg.isPhilosophy ? (
                          /* Special Philosophy Message Styling */
                          <div
                            className="max-w-[88%] px-4 py-4 text-[14px] leading-[1.5] whitespace-pre-wrap text-white"
                            style={{
                              background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
                              borderRadius: '20px',
                              border: '2px solid #FFE566',
                              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
                              wordBreak: 'break-word',
                            }}
                          >
                            {msg.content}
                          </div>
                        ) : (
                          <div
                            className={`max-w-[75%] px-4 py-2.5 text-[15px] leading-[1.35] whitespace-pre-wrap ${
                              msg.role === 'user' ? 'text-white' : 'text-black'
                            }`}
                            style={{
                              background: msg.role === 'user' ? '#007AFF' : '#e9e9eb',
                              borderRadius: msg.role === 'user'
                                ? '20px 20px 4px 20px'
                                : '20px 20px 20px 4px',
                              wordBreak: 'break-word',
                            }}
                          >
                            {msg.content}
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </AnimatePresence>

                  {isTyping && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex justify-start"
                    >
                      <div
                        className="px-4 py-3 flex items-center gap-1"
                        style={{ background: '#e9e9eb', borderRadius: '20px 20px 20px 4px' }}
                      >
                        <motion.div
                          className="w-2 h-2 rounded-full bg-gray-400"
                          animate={{ scale: [1, 1.2, 1] }}
                          transition={{ duration: 0.6, repeat: Infinity, delay: 0 }}
                        />
                        <motion.div
                          className="w-2 h-2 rounded-full bg-gray-400"
                          animate={{ scale: [1, 1.2, 1] }}
                          transition={{ duration: 0.6, repeat: Infinity, delay: 0.2 }}
                        />
                        <motion.div
                          className="w-2 h-2 rounded-full bg-gray-400"
                          animate={{ scale: [1, 1.2, 1] }}
                          transition={{ duration: 0.6, repeat: Infinity, delay: 0.4 }}
                        />
                      </div>
                    </motion.div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input Area */}
                <div
                  className="px-3 py-2 border-t"
                  style={{ background: '#f2f2f7', borderColor: 'rgba(0,0,0,0.1)' }}
                >
                  <div className="flex items-center gap-2 px-1">
                    <button
                      className="w-8 h-8 rounded-full flex items-center justify-center"
                      style={{ background: '#e9e9eb' }}
                    >
                      <span className="text-gray-500 text-xl font-light">+</span>
                    </button>

                    <div
                      className="flex-1 flex items-center rounded-full px-4 py-2"
                      style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.1)', minHeight: '36px' }}
                    >
                      <input
                        type="text"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                        placeholder="iMessage"
                        className="flex-1 bg-transparent outline-none text-[16px] text-black placeholder:text-gray-400"
                        style={{ caretColor: '#007AFF' }}
                      />
                    </div>

                    <motion.button
                      onClick={handleSendMessage}
                      disabled={!inputValue.trim() || isTyping}
                      whileTap={{ scale: 0.9 }}
                      className="w-8 h-8 rounded-full flex items-center justify-center disabled:opacity-30 transition-opacity"
                      style={{ background: inputValue.trim() ? '#007AFF' : '#e9e9eb' }}
                    >
                      <Send
                        size={16}
                        className={inputValue.trim() ? 'text-white' : 'text-gray-400'}
                        style={{ marginLeft: '-1px', marginTop: '-1px' }}
                      />
                    </motion.button>
                  </div>

                  <div className="flex justify-center pt-2 pb-1">
                    <div className="w-32 h-1 rounded-full" style={{ background: '#000' }} />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default CoachTabContent;
